import React, {useEffect, useRef, useState} from 'react'
import {
  useBasketStore,
  useContentStore,
  useCustomerStore,
  useGlobalStore,
  useProductStore,
  usePromotionStore,
  useRapidDeliveryStore
} from '../../store/hooks/useStore'
import {Box, Container, Flex, Stack} from '../../vanilla'
import { SlotContentType } from '../category'
import { DisplaySlots } from '../category/partials/display-slots'
import { Spinner } from '../../components/spinner'
import { usePageDataTracking } from '../../analytics/page-tracking'
import { BreadcrumbsContent } from '../../components/cms/partials/breadcrumbsContent'
import { PageHeader } from '../../components/cms/partials/pageHeader'
import { Layout } from '../../components/layout'
import { NoAvailableDealsMessage } from './no-available-deals-message'
import { flowResult } from 'mobx'
import { enoughProductsInPromotionGroup } from '../../utils/product-utils'
import { observer } from 'mobx-react'
import { StatusMap } from '../../types/commerce'
import { Seo } from '../../components/seo'
import PromotionCard from '../../components/cms/PromotionCard'
import { SLOT_SCHEMAS } from '../../components/cms/utils'

export const EXPLORER_PAGE_TYPE = 'Meal Deal Explorer'
export const MEAL_DEAL_EXPLORER_KEY = 'meal-deal-explorer'

export const MealDealExplorer = observer(() => {
  const sendPageData = usePageDataTracking()
  const {contentById, fetchItemByKey, fetchItems} = useContentStore()
  const {selectedPriceDayOffset, selectedRegion} = useGlobalStore()
  const {isRapidPricingActive} = useRapidDeliveryStore()
  const {productsById, loading, fetchProducts} = useProductStore()
  const {basket, status: basketStatus} = useBasketStore()
  const {customerInfo} = useCustomerStore()
  const {fetchMealDeals, promotions, activeMealDeals, upcomingMealDeals} = usePromotionStore()
  const pageTracked = useRef(false)
  const [explorerStatus, setExplorerStatus] = useState<'initial' | 'loading' | 'done'>('initial')
  const [explorerDealsFetched, setExplorerDealsFetched] = useState<boolean>(false)
  const [availableExplorerDeals, setAvailableExplorerDeals] = useState<string[]>([])
  const [customerAuthType, setCustomerAuthType] = useState<string>('')

  useEffect(() => {
    if (explorerDealsFetched || explorerStatus === 'loading') return
    async function fetchExplorerDeals() {
      setExplorerStatus('loading')

      await flowResult(fetchMealDeals())
      await flowResult(fetchItemByKey(MEAL_DEAL_EXPLORER_KEY))

      const mealDealKeys = [...activeMealDeals, ...upcomingMealDeals]
      await flowResult(fetchItems(mealDealKeys.map((key) => ({key}))))

      const productIds = activeMealDeals.flatMap((promo: string) => {
        return promotions[promo]?.productIds ?? []
      })

      const uniqueProductIds = [...new Set(productIds)]
      if (uniqueProductIds.length) {
        await flowResult(fetchProducts(uniqueProductIds, false))
      }

      setExplorerDealsFetched(true)
    }
    fetchExplorerDeals()
  }, [
    explorerDealsFetched,
    basket?.basketId,
    explorerStatus,
    fetchMealDeals,
    fetchItems,
    fetchItemByKey,
    fetchProducts,
    activeMealDeals,
    upcomingMealDeals,
    promotions
  ])

  useEffect(() => {
    // Return if we don't have an auth type yet or if the value hasn't changed
    if (!customerInfo?.authType || customerAuthType === customerInfo?.authType) return
    if (!customerAuthType) {
      setCustomerAuthType(customerInfo?.authType)
    } else {
      setCustomerAuthType(customerInfo?.authType || '')
      // slight delay to allow backend to process authentication change before fetching data
      setTimeout(() => {
        // clear state if forcing a refresh
        setAvailableExplorerDeals([])
        setExplorerStatus('initial')
        setExplorerDealsFetched(false)
        pageTracked.current = false
      }, 1200)
    }
  }, [customerInfo?.authType])

  useEffect(() => {
    if (loading || !explorerDealsFetched || basketStatus != StatusMap.IDLE) return
    const promos = []
    for (const promo of activeMealDeals) {
      const promotion = promotions[promo]
      if (!promotion) continue
      const dealHasValidProducts =
        Object.keys(productsById).length > 0 &&
        enoughProductsInPromotionGroup({
          promotion: promotion,
          productsIds: null,
          selectedPriceDayOffset: selectedPriceDayOffset ?? null,
          selectedRegion: selectedRegion,
          productsById: productsById,
          isRapidPricingActive
        }) === true
      if (dealHasValidProducts) {
        promos.push(promo)
      }
    }
    setAvailableExplorerDeals(promos)
    setExplorerStatus('done')
  }, [
    basketStatus,
    explorerDealsFetched,
    activeMealDeals,
    loading,
    promotions,
    productsById,
    selectedPriceDayOffset,
    selectedRegion,
    enoughProductsInPromotionGroup
  ])

  // Action Tracking
  useEffect(() => {
    if (explorerStatus === 'done' && !pageTracked.current) {
      pageTracked.current = true
      sendPageData(
        EXPLORER_PAGE_TYPE,
        (availableExplorerDeals?.length ?? 0) > 0 || (upcomingMealDeals?.length ?? 0) > 0
          ? 'Available'
          : 'Unavailable'
      )
    }
  }, [sendPageData, explorerStatus])

  if (explorerStatus !== 'done') {
    return (
      <Flex justify="center" align="center" style={{minHeight: '200px'}}>
        <Spinner color="primary" />
      </Flex>
    )
  }

  const hasPromotions =
    (availableExplorerDeals?.length ?? 0) > 0 || (upcomingMealDeals?.length ?? 0) > 0

  const content = contentById[MEAL_DEAL_EXPLORER_KEY]
  const breadCrumbs = content?.BreadCrumb || []
  // Precalculate the starting index for active and upcoming promotions to be used for analytics tracking.
  const slotCount = content?.slots?.length || 0
  const activePromotionStartIndex = slotCount + (availableExplorerDeals?.length > 0 ? 1 : 0)
  const upcomingPromotionStartIndex =
    activePromotionStartIndex + (upcomingMealDeals?.length > 0 ? 1 : 0)

  // Test selectors to inject into the PageHeader component
  const headerDataTestSelectors = {
    heading: 'meal-deal-page-title'
  }

  return (
    <>
      <Layout>
        <Seo
          title={content?.seo?.pageTitle}
          description={content?.SEO?.pageDescription}
          canonical='/meal-deals'
        />
        <Stack paddingBottom={['28px', '36px']}>
          <Container paddingX={['8px']}>
            <BreadcrumbsContent breadCrumb={breadCrumbs}></BreadcrumbsContent>
            <PageHeader
              pageHeading={content?.pageHeading}
              dataTestSelectors={headerDataTestSelectors}
            ></PageHeader>
            {content?.slots?.map(
              (slotGroup: SlotContentType, idx: number) =>
                slotGroup?._meta?.schema === SLOT_SCHEMAS.MEAL_DEALS_EXPLORER_TOP_SLOT && (
                  <DisplaySlots slots={slotGroup} slotIndex={idx + 1} key={`${slotGroup?._meta?.deliveryId}-${idx}`} />
                ),
            )}
            {hasPromotions ? (
              <Box
                display="grid"
                gridTemplateColumns={['1x', '3x']}
                gap="16px"
                justifyContent={'center'}
                marginTop={['16px', '28px']}
              >
                {availableExplorerDeals?.map((promo: string, idx: number) => (
                  <PromotionCard
                    promoID={promo}
                    slotIndex={activePromotionStartIndex}
                    listIndex={idx + 1}
                    key={promo}
                    slotName={MEAL_DEAL_EXPLORER_KEY}
                  />
                ))}
                {upcomingMealDeals?.map((promo: string, idx: number) => (
                  <PromotionCard
                    promoID={promo}
                    isUpcoming={true}
                    slotIndex={upcomingPromotionStartIndex}
                    listIndex={idx + 1}
                    key={promo}
                    slotName={MEAL_DEAL_EXPLORER_KEY}
                  />
                ))}
              </Box>
            ) : (
              <NoAvailableDealsMessage isExplorer isMaxLimitReached={false} />
            )}
          </Container>
        </Stack>
      </Layout>
    </>
  )
})
